import React from "react";
import { Box } from "@material-ui/core";
import { PhoneNumberField, withOptionalPhone } from "shared/PhoneNumberInput";
import { CTextField, CSelect, HFlex, ProgressButton, Text, DialogSuccessNotice, useErrorContext } from "shared";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Send } from "assets";
import { delay } from "utils/misc";
import { Country } from "utils/market";
import { FORM_NOTIFICATION_DELAY } from "theme";
import { useMountState } from "utils/hooks";
import useInviteBillingAdmin, { InviteBillingAdminInput } from "routes/other/queries/useInviteBillingAdmin";
import { timeZonesUS } from "../helpers";
import { useHealthCareOrganizations } from "routes/healthCareOrganizations/queries/useHealthCareOrganizations";

interface Props {
  onClose: () => void;
}

export const InviteBillingAdminForm: React.VFC<Props> = ({ onClose }) => {
  const form = useForm<InviteBillingAdminInput>();
  const { t } = useTranslation();
  const setError = useErrorContext();
  const getMountState = useMountState();
  const { data: hcos } = useHealthCareOrganizations();

  const mappedHCOs =
    hcos
      ?.map(hco => ({ value: hco.id, label: hco.name }))
      ?.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase())) || [];

  const { mutateAsync, isLoading, isError, data } = useInviteBillingAdmin();

  const submit = form.handleSubmit(async fields => {
    try {
      await mutateAsync(withOptionalPhone(fields));
      if (!getMountState()) return;
      await delay(FORM_NOTIFICATION_DELAY);
      if (!getMountState()) return;
      onClose();
    } catch (err) {
      setError(t`errors.could_not_send_invite`, err);
    }
  });

  if (!isError && data)
    return (
      <DialogSuccessNotice
        heading={t("patients.invite_sent")}
        body={`${t("form.invitation_successfully_sent_to")} ${data.email}`}
        onClose={onClose}
      />
    );

  return (
    <Box mx={3}>
      <Text heading bold mb={3} id="invite-billing-admin">
        {t("form.invite_billing_admin")}
      </Text>
      <FormProvider {...form}>
        <form onSubmit={submit}>
          <CTextField
            required
            name="email"
            autoFocus
            label={t("form.email")}
            type="email"
            margin="normal"
            size="small"
            fullWidth
          />

          <PhoneNumberField containerStyle={{ width: "100%", margin: "16px 0 8px" }} inputStyle={{ width: "100%" }} />

          <HFlex gap={2}>
            <CTextField
              fullWidth
              name="first_name"
              label={t("form.first_name")}
              margin="normal"
              size="small"
              required
            />
            <CTextField fullWidth name="last_name" label={t("form.last_name")} margin="normal" size="small" required />
          </HFlex>

          <CSelect
            name="health_care_organization_id"
            label={t("form.health_care_organization")}
            items={mappedHCOs}
            margin="normal"
            size="small"
            required
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          <CTextField
            name="country"
            label={t("form.country")}
            margin="normal"
            size="small"
            required
            fullWidth
            disabled
            defaultValue={Country.US}
          />

          <CSelect
            name="timezone"
            label={t("form.home_timezone")}
            items={timeZonesUS}
            margin="normal"
            size="small"
            required
            fullWidth
          >
            {({ label }) => <Text>{label}</Text>}
          </CSelect>

          <HFlex justifyContent="center" my={6}>
            <ProgressButton loading={isLoading} endIcon={<Send width={20} />}>
              {t("buttons.send_invitation")}
            </ProgressButton>
          </HFlex>
        </form>
      </FormProvider>
    </Box>
  );
};
